import { z } from 'zod';

/**
 * Environment schema for the DragonIS CDR Generator.
 *
 * Mirrors `.env.example` exactly. Keys with defaults here are optional in the
 * input dict; all other keys are required. Unknown keys are rejected by
 * `.strict()` — a typo in the deployment config should fail loud, not be
 * silently ignored.
 */
const envSchema = z
  .object({
    DATABASE_URL: z.string().min(1),

    // The storage adapter is S3-compatible. The env vars are named R2_* for
    // historical reasons (Cloudflare R2 was the original target) but any
    // S3-compatible backend works — Backblaze B2, AWS S3, DigitalOcean Spaces,
    // etc. R2_REGION exists because non-R2 providers embed region in the SigV4
    // credential scope and will reject 'auto' (B2 verifies region matches the
    // endpoint's geographic subdomain, AWS verifies against its region list).
    R2_ACCOUNT_ID: z.string().min(1),
    R2_ACCESS_KEY_ID: z.string().min(1),
    R2_SECRET_ACCESS_KEY: z.string().min(1),
    R2_BUCKET: z.string().min(1),
    R2_ENDPOINT: z.string().min(1),
    R2_REGION: z.string().min(1).default('auto'),

    SESSION_SECRET: z.string().min(1),

    // Microsoft Entra ID (OIDC) — all required. The schema is .strict() and
    // shared by API + cron; the cron never calls Entra but the strict loader
    // requires these present, so the cron is given the same values (arch §9.3).
    ENTRA_TENANT_ID: z.string().min(1),
    ENTRA_CLIENT_ID: z.string().min(1),
    ENTRA_CLIENT_SECRET: z.string().min(1),
    ENTRA_REDIRECT_URI: z.string().url(),
    BOOTSTRAP_ADMIN_EMAIL: z.string().email(),

    RETENTION_YEARS: z.coerce.number().int().positive().default(7),
    PDF_CONCURRENCY: z.coerce.number().int().positive().default(4),

    // Sale price = carrier cost × PRICING_MARKUP, applied per row then summed.
    // 1.3 = 30% margin over the carrier's CDR cost column.
    PRICING_MARKUP: z.coerce.number().positive().default(1.3),

    LOG_LEVEL: z.string().min(1).default('info'),
    CORS_ORIGIN: z.string().min(1),

    PORT: z.coerce.number().int().positive().default(3000),
    NODE_ENV: z.enum(['development', 'test', 'production']),
  })
  .strict();

export type Env = z.infer<typeof envSchema>;

/**
 * Parse and validate an environment dict against the schema.
 *
 * Accepts any Record (defaults to `process.env`) so tests can inject a dict
 * without touching the real process environment. Dotenv-style auto-loading is
 * intentionally out of scope here — that is wired up by a higher layer.
 *
 * On failure, throws an Error whose message enumerates the offending keys so a
 * developer can find the problem without digging into the zod issue tree.
 */
export function loadEnv(source: Record<string, string | undefined> = process.env): Env {
  const result = envSchema.safeParse(source);
  if (result.success) {
    return result.data;
  }

  const details = result.error.issues
    .map((issue) => {
      const key = issue.path.length > 0 ? issue.path.join('.') : '(root)';
      return `${key} (${issue.message})`;
    })
    .join('; ');

  throw new Error(`Missing or invalid env: ${details}`);
}
